/**
 * Tiny fetch wrapper. Cookies carry the session — every request goes
 * with `credentials: "include"`. Methods throw ApiError on non-2xx.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${status}: ${JSON.stringify(body)}`);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, parsed ?? text);
  return parsed as T;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

// Domain types — kept loose since Phase 2 ships an API that the UI
// shapes against. We only narrow the fields the UI actually reads.

export type Me = {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerifiedAt: string | null;
  };
  /** Each role grant carries the building it's bound to plus a
   *  resolved building name so the nav profile dropdown can render
   *  "Property Manager · The Elm at Clark" without a follow-up
   *  fetch. buildingName is null only when the building was deleted
   *  while a stale role grant survived. */
  roles: { code: string; buildingId: string; buildingName: string | null }[];
  /** Active unit ownerships joined to building so the nav dropdown
   *  can render "Owner · Unit 1108 at The Elm at Clark". */
  ownerships: Array<{
    unitId: string;
    unitNumber: string;
    buildingId: string;
    buildingName: string;
  }>;
};

export type Project = {
  id: string;
  buildingId: string;
  unitId: string;
  categoryId: string;
  projectTypeId: string;
  statusId: string;
  title: string;
  scopeOfWork: string;
  proposedStartDate: string | null;
  proposedEndDate: string | null;
  isEmergency: boolean;
  requiresRiserShutdown: boolean;
  requiresTelecomShutdown: boolean;
  requiresCityPermit: boolean;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProjectListRow = {
  id: string;
  title: string;
  buildingId: string;
  unitId: string;
  projectTypeId: string;
  categoryId: string;
  statusId: string;
  statusCode: string;
  submittedBy: string;
  submittedAt: string | null;
  isEmergency: boolean;
  createdAt: string;
  updatedAt: string;
};

export type Requirement = {
  requirementId: string;
  documentTypeId: string;
  documentTypeCode: string;
  documentTypeName: string;
  requiredFrom: string;
  isRequired: boolean;
  fulfilled: boolean;
  uploadedDocumentIds: string[];
};

export type Readiness = { ready: boolean; missing: Requirement[] };

export type VotingSummary = {
  state: "no_board_step" | "open" | "finalized";
  eligibleVoters?: number;
  votesCast?: number;
  votingWindowEndsAt?: string | null;
  decision?: string | null;
};

export type ProjectDeadlines = {
  earliestStartDate: string;
  constraints: Array<{ label: string; days: number }>;
  insurancePolicyExpirationDate: string | null;
  votingWindowEndsAt: string | null;
  riserShutdownEarliestDate: string | null;
};

export type ProjectDetail = {
  data: Project;
  requirements: Requirement[];
  readiness: Readiness;
  voting: VotingSummary;
  deadlines: ProjectDeadlines;
  /** Category letter (A/B/C) for the project's primary type. */
  categoryCode: "A" | "B" | "C" | null;
  /** Honest timeline forecast — submit-to-decision history for this
   *  building × category over the last 6 months. Nulls when the
   *  building doesn't have enough history yet. */
  timelineForecast: {
    sampleSize: number;
    windowDays: number;
    avgDays: number | null;
    medianDays: number | null;
    p90Days: number | null;
  };
  /** Comment_posted notifications for the principal on this project
   *  that haven't been marked read. Drives the "N new comments" pill
   *  in the project header. */
  unreadCommentCount: number;
  /** All project types attached to the project (primary first). */
  projectTypes: Array<{ id: string; name: string; code: string }>;
  /** True when the signed-in user is a current owner of the project's unit. */
  isOwnerOfUnit: boolean;
  /** True when the signed-in user is a Board Member AND owns the unit
   *  on this project (so their Board Member role is auto-recused for
   *  this project's review). */
  isRecusedHere: boolean;
  /** Status code (e.g. "draft", "submitted", "approved"). */
  statusCode: string;
  /** True when the signed-in user is the unit owner AND the project's
   *  current status accepts owner edits (draft, pending_owner_revisions,
   *  resubmission_required). */
  isEditableByOwner: boolean;
  /** White-glove submission attribution. Null on regular owner
   *  submissions. When non-null the project page renders a
   *  "Created by [staff] on behalf of [owner]" banner. */
  whiteGlove: {
    onBehalfOfUserId: string;
    onBehalfOfName: string | null;
    actorUserId: string;
    actorName: string | null;
  } | null;
  /** Every active owner of the project's unit. A unit can have
   *  more than one (spouses, joint ownership). Every owner has
   *  the same edit + review capabilities and gets the project's
   *  email notifications. */
  owners: Array<{
    userId: string;
    name: string;
    ownershipType: string;
  }>;
};

export type CommentRow = {
  id: string;
  projectId: string;
  documentId: string | null;
  authorUserId: string;
  body: string;
  visibility: "all" | "internal";
  parentCommentId: string | null;
  createdAt: string;
  editedAt: string | null;
};

export type CommentSummary = {
  headline: string;
  topics: Array<{
    topic: string;
    status: string;
    awaitingFrom: "owner" | "staff" | "board" | "contractor" | "nobody";
  }>;
  outstandingFor: {
    party: "owner" | "staff" | "board" | "contractor" | "nobody";
    items: string[];
  };
  digest: string;
};

export type CommentSummaryEnvelope = {
  data: CommentSummary | null;
  threshold: number;
  commentCount: number;
};

export type EmailPreferenceItem = {
  key: string;
  label: string;
  hint: string;
  defaultEnabled: boolean;
  enabled: boolean;
};

export type EmailPreferencesEnvelope = {
  data: {
    unsubscribedAll: boolean;
    items: EmailPreferenceItem[];
  };
};

export type Notification = {
  id: string;
  templateCode: string;
  payload: Record<string, unknown>;
  status: "queued" | "sent" | "failed" | "read";
  channel: "in_app" | "email" | "sms";
  projectId: string | null;
  createdAt: string;
};

export type LiabilityPolicy = {
  id: string;
  unitId: string;
  carrierName: string;
  policyNumber: string;
  perIncidentLimitCents: string;
  effectiveDate: string;
  expirationDate: string;
  validationStatus: string;
};

export type FeeRow = {
  id: string;
  kind: string;
  description: string;
  amountCents: number;
  status: "due" | "received" | "refunded" | "withheld" | "waived";
  dueAt: string;
  receivedAt: string | null;
  notes: string | null;
};

export type FeeSummary = {
  dueCents: number;
  receivedCents: number;
  pendingItems: number;
};

export type RiserShutdownRow = {
  id: string;
  riserId: string;
  requestedDate: string;
  scheduledDate: string | null;
  area: string;
  purpose: string;
  feeCents: number;
  feeStatus: string;
  rescheduleCount: number;
  disposition: "pending" | "approved" | "rejected";
};

export type IndemnityState = {
  data: { id: string; signedAt: string; signerFullName: string } | null;
  text: string;
  textHash: string;
};

export type RulesAckState = {
  data: { id: string; ownerSignedAt: string | null } | null;
  text: string;
  textHash: string;
  rulesUrl: string;
};

export type ScopeRefineOutput = {
  refinedScope: string;
  summary: string;
  addedItems: string[];
};

export type LinkPreviewView = {
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  fetchedAt: string;
  errorCode: string | null;
  /** True when content_type belongs to the importable set (PDF /
   *  png / jpg / webp / gif). The card uses this to render the
   *  "save to project documents" action. */
  importable: boolean;
};

export type MyUnitRow = {
  id: string;
  unitNumber: string;
  floor: number | null;
  unitType: string | null;
  location: string | null;
  isCombinedUnit: boolean;
  hasWasherDryer: boolean;
  ownerNotes: string;
  buildingId: string;
  buildingName: string;
  riserId: string;
  riserCode: string;
};

export type UpdateMyUnitInput = {
  unitType?: string | null;
  location?: string | null;
  hasWasherDryer?: boolean;
  ownerNotes?: string;
};

export type ExplainedCondition = {
  title: string;
  meaning: string;
  howToAddress: string;
  link?: string;
  severity: "blocker" | "warning" | "info";
};
export type ConditionsExplanation = {
  summary: string;
  conditions: ExplainedCondition[];
  whoToContact: string | null;
};
export type ConditionsExplanationEnvelope = {
  data: ConditionsExplanation | null;
  rawConditions?: string | null;
  decidedAt?: string;
  reason?: string;
};

export type Anomaly = {
  kind: string;
  title: string;
  detail: string;
  severity: "high" | "medium" | "low";
  suggestedAction: string;
};
export type AnomalyOutput = {
  summary: string;
  anomalies: Anomaly[];
};

export type PreflightStep = {
  title: string;
  action: string;
  why: string;
  link?: string;
  severity: "blocker" | "warning" | "nice_to_have";
};
export type PreflightOutput = {
  summary: string;
  steps: PreflightStep[];
};

export type ManagementQueueRow = {
  id: string;
  title: string;
  isEmergency: boolean;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  statusCode: string;
  typeName: string;
  typeCode: string;
  unitNumber: string;
  ownerName: string;
  enteredCurrentStatusAt: string;
  daysInStatus: number;
  stale: boolean;
  activeStep: {
    stepType: "management_review" | "board_review";
    status: string;
    votingWindowEndsAt: string | null;
  } | null;
  decision: string | null;
  contractors: {
    total: number;
    opened: number;
    uploaded: number;
    staleInvites: number;
  };
  urgencyScore: number;
  urgencyReasons: Array<{
    kind: string;
    label: string;
    severity: "critical" | "high" | "medium";
  }>;
};

export type BoardQueueActiveRow = {
  approvalStepId: string;
  projectId: string;
  projectTitle: string;
  typeName: string;
  unitNumber: string;
  isEmergency: boolean;
  votingWindowEndsAt: string | null;
  eligibleVoters: number;
  votesCast: number;
  autoRecusedHere: boolean;
  myVote: string | null;
};

export type BoardQueueRecentRow = {
  approvalStepId: string;
  projectId: string;
  projectTitle: string;
  typeName: string;
  unitNumber: string;
  decision: string;
  decidedAt: string;
  myVote: string | null;
};

export type BoardQueue = {
  awaitingMyVote: BoardQueueActiveRow[];
  alreadyVoted: BoardQueueActiveRow[];
  recentlyDecided: BoardQueueRecentRow[];
};

export type ContractorStatus = {
  projectContractorId: string;
  companyId: string;
  companyName: string;
  companyDiscipline: string;
  companyEmail: string | null;
  role: string;
  addedAt: string;
  link: {
    issuedAt: string;
    expiresAt: string;
    exchangedAt: string | null;
    revokedAt: string | null;
    invitedEmail: string;
  } | null;
  documentCount: number;
  latestUploadAt: string | null;
  status: "no_invite" | "invited" | "viewed" | "uploading";
};

export type SavedChatReply = {
  id: string;
  content: string;
  actions: Array<{ label: string; to?: string; prompt?: string }> | null;
  savedAt: string;
};

export type ContractorDirectoryRow = {
  companyId: string;
  name: string;
  contactName: string | null;
  discipline: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  businessLicenseNo: string | null;
  isActive: boolean;
  /** Distinct portal projects plus priorProjectCount. */
  projectCount: number;
  /** Portal projects whose status is completed/closed (prior history
   *  is never counted here). */
  completedProjectCount: number;
  /** Projects from the paper records that predate the portal. */
  priorProjectCount: number;
  /** Per-job scope summaries from the paper records (unit numbers
   *  scrubbed) — what the company actually did on each request. */
  priorJobSummaries: string[];
  /** Roles filled across portal engagements (general/plumbing/electrical/hvac). */
  roles: string[];
  /** Distinct project type names — portal projects plus prior records. */
  workTypes: string[];
  /** Null for companies known only from the paper records. */
  firstEngagedAt: string | null;
  lastEngagedAt: string | null;
  /** Anonymous owner ratings: average (1 decimal) and count. */
  ratingAvg: number | null;
  ratingCount: number;
  /** The signed-in user's own rating (server-resolved; never shown
   *  to anyone else), null if they haven't rated. */
  myStars: number | null;
  /** True when the signed-in user engaged this company on one of
   *  their unit's projects and may rate it. */
  canRate: boolean;
};

export type BoardVotingDetail = {
  approvalStepId: string;
  status: string;
  votingWindowEndsAt: string | null;
  eligibleVoters: number;
  votesCast: number;
  finalized: boolean;
  decision: string | null;
  // staff-only fields
  tally?: Record<string, number>;
  votes?: {
    voterUserId: string;
    vote: string;
    conditions: string | null;
    reason: string | null;
    castAt: string;
  }[];
  // board-member fields
  myVote?: {
    vote: string;
    conditions: string | null;
    reason: string | null;
    castAt: string;
  } | null;
  finalTally?: Record<string, number> | null;
};
